import { useEffect, useState, useId } from "react";

import { FileText, Upload, X } from "lucide-react";

import { getFileUrl } from "../../lib/api";
import SecureImage from "./SecureImage";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Label } from "../ui/label";

const DocumentUploadInput = ({
  value, // Current value (can be a URL path or base64)
  onChange, // Callback when file is selected or removed
  label = "Attachment",
  id,
  accept = "image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  multiple = false,
  className = "",
  helperText = "",
  required = false
}) => {
  const reactId = useId();
  const sanitizedId = reactId.replace(/:/g, "");
  const inputId = id || `upload-${sanitizedId}`;
  const [previews, setPreviews] = useState([]);

  useEffect(() => {
    // Sync previews with value prop
    if (!value) {
      setPreviews([]);
      return;
    }

    const valueArray = Array.isArray(value) ? value : [value];

    const newPreviews = valueArray
      .map((val) => {
        if (!val) return null;

        // If it's a string, determine type and preview URL
        if (typeof val === "string") {
          const isBase64 = val.startsWith("data:");
          const isUrl = val.startsWith("http") || val.startsWith("/");

          let type = "file";
          let previewUrl = val;

          if (isBase64) {
            if (val.includes("image/")) type = "image";
            else if (val.includes("application/pdf")) type = "pdf";
            else if (val.includes("word")) type = "word";
            else if (val.includes("excel") || val.includes("spreadsheet")) type = "excel";
          } else if (isUrl) {
            previewUrl = getFileUrl(val);

            const extension = val.split(".").pop().toLowerCase();
            if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) type = "image";
            else if (extension === "pdf") type = "pdf";
            else if (["doc", "docx"].includes(extension)) type = "word";
            else if (["xls", "xlsx"].includes(extension)) type = "excel";
          }

          return { type, preview: previewUrl, original: val };
        }
        return null;
      })
      .filter(Boolean);

    setPreviews(newPreviews);
  }, [value]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const readFilesPromises = files.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result);
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(readFilesPromises).then((results) => {
      if (multiple) {
        const currentValues = Array.isArray(value) ? value : value ? [value] : [];
        onChange([...currentValues, ...results]);
      } else {
        onChange(results[0]);
      }
    });

    // Reset input value to allow selecting same file again
    e.target.value = "";
  };

  const handleRemove = (index) => {
    if (multiple) {
      const currentValues = [...value];
      currentValues.splice(index, 1);
      onChange(currentValues);
    } else {
      onChange(null);
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case "pdf":
        return "text-red-500";
      case "excel":
        return "text-green-600";
      case "word":
        return "text-blue-600";
      default:
        return "text-slate-400";
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <Label htmlFor={inputId} className="flex items-center gap-1.5">
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
      )}

      {!multiple && previews.length > 0 ? (
        <div className="relative group inline-block">
          <div className="border border-slate-200 dark:border-yale-blue-800 rounded-lg overflow-hidden bg-slate-50 dark:bg-yale-blue-900/30">
            <div className="h-40 w-full min-w-[200px] flex items-center justify-center p-2">
              {previews[0].type === "image" ? (
                <SecureImage src={previews[0].preview} alt="Preview" className="max-h-36 max-w-full object-contain rounded" />
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <FileText className={cn("w-12 h-12", getFileIcon(previews[0].type))} />
                  <span className="text-xs font-medium text-slate-500 max-w-[150px] truncate">
                    {previews[0].original.split("/").pop()}
                  </span>
                </div>
              )}
            </div>
          </div>
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute -top-2 -right-2 h-6 w-6 rounded-full shadow-lg z-10"
            onClick={() => handleRemove(0)}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <label
            htmlFor={inputId}
            className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 dark:border-yale-blue-800 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-yale-blue-800/30 transition-colors bg-white dark:bg-transparent"
          >
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <Upload className="w-8 h-8 text-slate-400 mb-2" />
              <p className="text-sm text-slate-500 font-medium">Click to upload document</p>
              <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider font-semibold">Images, PDF, Word, Excel</p>
            </div>
            <input
              id={inputId}
              type="file"
              accept={accept}
              className="hidden"
              onChange={handleFileChange}
              multiple={multiple}
              required={required && previews.length === 0}
            />
          </label>

          {multiple && previews.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {previews.map((preview, index) => (
                <div
                  key={index}
                  className="relative group border border-slate-200 dark:border-yale-blue-800 rounded-lg overflow-hidden bg-slate-50 dark:bg-yale-blue-900/30"
                >
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-1 right-1 h-5 w-5 rounded-full shadow-lg z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleRemove(index)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                  <div className="h-24 w-full flex items-center justify-center p-2">
                    {preview.type === "image" ? (
                      <SecureImage src={preview.preview} alt="Preview" className="w-full h-full object-cover rounded" />
                    ) : (
                      <div className="flex flex-col items-center gap-1">
                        <FileText className={cn("w-8 h-8", getFileIcon(preview.type))} />
                        <span className="text-[8px] font-medium text-center truncate max-w-[80px]">
                          {preview.original.split("/").pop()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {helperText && <p className="text-[10px] text-slate-500 italic">{helperText}</p>}
    </div>
  );
};

export default DocumentUploadInput;
